/**
 * Browser-side signup conversion for the ad platforms.
 *
 * Signups are already tracked server-side (`USER_REGISTERED` in auth.service),
 * but Google and Meta can only attribute a conversion to an ad click if the
 * event fires in the user's browser, in their session. Hence this.
 *
 * Fires at most once per browser session. Both entry points (the /welcome page
 * and the inline path taken when a build is already starting) call this, so the
 * guard is what keeps a single signup from counting twice.
 */

const FIRED_KEY = 'dunea:signup_conversion_fired';

/** Set these to the values Google Ads shows when you create the conversion action. */
const GOOGLE_ADS_ID = import.meta.env.VITE_GOOGLE_ADS_ID as string | undefined;
const GOOGLE_ADS_SIGNUP_LABEL = import.meta.env.VITE_GOOGLE_ADS_SIGNUP_LABEL as string | undefined;

type Gtag = (...args: unknown[]) => void;

function alreadyFired(): boolean {
  try {
    return sessionStorage.getItem(FIRED_KEY) === '1';
  } catch {
    // Private mode / storage disabled — better to risk a duplicate than to
    // silently drop every conversion.
    return false;
  }
}

function markFired() {
  try {
    sessionStorage.setItem(FIRED_KEY, '1');
  } catch {
    // ignore
  }
}

export function fireSignupConversion() {
  if (alreadyFired()) {
    return;
  }

  markFired();

  const gtag = (window as unknown as { gtag?: Gtag }).gtag;

  if (gtag && GOOGLE_ADS_ID && GOOGLE_ADS_SIGNUP_LABEL) {
    gtag('event', 'conversion', {
      send_to: `${GOOGLE_ADS_ID}/${GOOGLE_ADS_SIGNUP_LABEL}`,
    });
  }

  const fbq = (window as unknown as { fbq?: Gtag }).fbq;

  if (fbq) {
    fbq('track', 'CompleteRegistration');
  }
}
